import PropTypes from 'prop-types';
import Logger from '@medflex/utils/Logger';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { oidcClientIds } from './GenericUserManager';
import MobileUserManager from './MobileUserManager';
import MobileUserManagerLegacy from './legacy/MobileUserManagerLegacy';
import LoadingView from '../LoadingView';

const checkLegacyAuthentication = async () => {
  try {
    const isLegacy = await window.top.flutter_inappwebview.callHandler(
      'authentication_isLegacyAuthentication',
    );

    Logger.log(
      `[MobileUserManagerSwitch] - isLegacy ${JSON.stringify(isLegacy)}, typeof ${typeof isLegacy}`,
    );

    // Returns null, if callhandler does not exist
    return isLegacy === null ? true : !!isLegacy;
  } catch (ex) {
    // Expected behavior for older versions of the mobile app, because it does not implement this function
    Logger.log(`[MobileUserManagerSwitch] - isLegacy error`, ex);
    return true;
  }
};

const MobileUserManagerSwitch = ({
  children,
  basePath,
  silentRedirectUri,
  oidcAuthority,
  clientId,
  postLogoutRedirectUri,
  onSessionLoaded,
  silentRenewDisabled,
  externalExpired,
}) => {
  const { t } = useTranslation('components');
  const [isLegacy, setIsLegacy] = useState(null);
  const [webviewReady, setIsWebviewReady] = useState(false);

  useEffect(() => {
    if (!webviewReady) {
      const handleWebViewReady = () => {
        Logger.info(`[MobileUserManagerSwitch] - flutter inappwebview ready`);
        setIsWebviewReady(true);
      };

      // we check if the callHandler already exists. That way we ensure that we did not miss the event
      // because it has already been fired before the component rendered
      if (window.top.flutter_inappwebview && window.top.flutter_inappwebview.callHandler) {
        handleWebViewReady();
        return () => {};
      }

      Logger.info(
        `[MobileUserManagerSwitch] - flutter inappwebview not yet ready, waiting for flutterInAppWebViewPlatformReady event`,
      );

      window.top.addEventListener('flutterInAppWebViewPlatformReady', handleWebViewReady);
      return () => {
        window.top.removeEventListener('flutterInAppWebViewPlatformReady', handleWebViewReady);
      };
    }

    return () => {};
  }, [webviewReady]);

  useEffect(() => {
    if (webviewReady && isLegacy === null) {
      (async () => {
        setIsLegacy(await checkLegacyAuthentication());
      })();
    }
  }, [webviewReady, isLegacy]);

  useEffect(() => {
    if (isLegacy !== null) Logger.info(`[MobileUserManagerSwitch] - isLegacy returned ${isLegacy}`);
  }, [isLegacy]);

  if (!webviewReady || isLegacy === null)
    return (
      <LoadingView
        title={t('UserManager.LoadingView.title')}
        message={t('UserManager.LoadingView.message')}
      />
    );

  if (isLegacy)
    return (
      <MobileUserManagerLegacy
        basePath={basePath}
        silentRedirectUri={silentRedirectUri}
        oidcAuthority={oidcAuthority}
        clientId={clientId}
        postLogoutRedirectUri={postLogoutRedirectUri}
        onSessionLoaded={onSessionLoaded}
        silentRenewDisabled={silentRenewDisabled}
        externalExpired={externalExpired}
      >
        {children}
      </MobileUserManagerLegacy>
    );

  return (
    <MobileUserManager
      basePath={basePath}
      onSessionLoaded={onSessionLoaded}
      silentRenewDisabled={silentRenewDisabled}
      externalExpired={externalExpired}
      oidcAuthority={oidcAuthority}
      silentRedirectUri={silentRedirectUri}
      clientId={clientId}
      postLogoutRedirectUri={postLogoutRedirectUri}
    >
      {children}
    </MobileUserManager>
  );
};

// ######################################################################################
// ## PropTypes
// ######################################################################################

MobileUserManagerSwitch.propTypes = {
  children: PropTypes.node.isRequired,
  basePath: PropTypes.string.isRequired,
  oidcAuthority: PropTypes.string.isRequired,
  clientId: PropTypes.oneOf(Object.values(oidcClientIds)).isRequired,
  postLogoutRedirectUri: PropTypes.string.isRequired,
  onSessionLoaded: PropTypes.func,
  silentRenewDisabled: PropTypes.bool,
  externalExpired: PropTypes.bool,
  silentRedirectUri: PropTypes.string.isRequired,
};

MobileUserManagerSwitch.defaultProps = {
  onSessionLoaded: () => {},
  silentRenewDisabled: false,
  externalExpired: false,
};

export default MobileUserManagerSwitch;
